/**
 * Body strength (owner, Sep 6 — Q59: "build and height affect play: basic strength from body / height; gyms with a workout that
 * takes a while for a slight boost that wears off; a one-time fee or a membership"). Strength scales what you carry before it
 * slows you, the melee swing, and the sprint limit; the slim build runs a little faster instead. A gym workout (`rules.gymSeconds`
 * inside the gym) gives a boost for `rules.gymBoostDays` game days; a membership makes the workouts free for a month.
 *
 * (owner, Sep 23 — his daughter's request: "different workouts to select from: upper body, lower body, full body, cardio, with
 * different effects — upper: carrying and melee; lower: run speed; cardio: endurance — and stamina for players: running and
 * fighting exhaust it, resting recovers it") Four workouts, each its own boost with its own clock; full body is half of each.
 * Stamina is a player's 0…1 meter: a sprint and a melee swing spend it, walking brings it back slowly and standing still fast;
 * run it dry and you are winded — no sprint, weaker swings — until it is back to a quarter. Cardio makes it last longer and
 * come back sooner. Pure functions here; game.ts steps them and the HUD shows the meter.
 *
 * (owner's daughter, Sep 25 — an idea report: "salille tekemistä", stuff to do at the gym) Two more on the board: the **punching
 * bag** — a timing game, a punch on each beat (a click, K, Ctrl or the fire button), the share of beats landed is a melee boost — and
 * the **sauna** — löyly on the stones: it heals, fills the stamina and makes you thirsty. The bag's best of the week per world is a
 * leaderboard (`boardAdd`); a membership comes with a card in the pockets.
 */
import type { Look } from './cosmetics';

/** strength by build (the body cosmetic): a broad body carries a quarter more, a slim one a bit less */
export const BODY_STRENGTH: Record<string, number> = { body_slim: 0.85, body_average: 1, body_stocky: 1.1, body_broad: 1.25 };
/** the slim build's foot speed (and the broad one's) */
export const BODY_SPEED: Record<string, number> = { body_slim: 1.05, body_average: 1, body_stocky: 1, body_broad: 0.96 };
export const WORKOUT_PRICE = 15, MEMBERSHIP_PRICE = 200, MEMBERSHIP_DAYS = 30;
export function bodyStrength(look: Look | undefined): number { return BODY_STRENGTH[look?.body ?? ''] ?? 1; }
export function bodySpeed(look: Look | undefined): number { return BODY_SPEED[look?.body ?? ''] ?? 1; }

/** the workouts at a gym's board */
export type Workout = 'upper' | 'lower' | 'full' | 'cardio' | 'boxing' | 'sauna';
export const WORKOUTS: { id: Workout; name: string; does: string }[] = [
  { id: 'upper', name: 'Upper body', does: 'carry more, hit harder' },
  { id: 'lower', name: 'Lower body', does: 'run faster' },
  { id: 'cardio', name: 'Cardio', does: 'more stamina' },
  { id: 'full', name: 'Full body', does: 'half of each' },
  { id: 'boxing', name: 'Punching bag', does: 'hit on the beat, swing harder' },
  { id: 'sauna', name: 'Sauna', does: 'löyly: heals and rests you' },
];
/** what a boost is worth at full: strength +15 %, running +8 %, endurance +50 %; (Sep 25) the bag's melee +30 % for a perfect round */
export const BOOST = { upper: 0.15, lower: 0.08, cardio: 0.5, melee: 0.3 };
export type BoostKey = keyof typeof BOOST;
/** a boost: until when (game time), and how much of its full effect (full body gives a half) */
export interface Boost { until: number; amt: number }
export type GymBoosts = Partial<Record<BoostKey, Boost>>;
/** the part of a boost in force now (0 when it has run out) */
export function boostOf(g: GymBoosts | undefined, k: BoostKey, time: number): number { const b = g?.[k]; return b && time < b.until ? b.amt : 0; }
/** the boosts after a finished workout: its own keys (a full body: all three at a half) until `until`; a stronger boost still running is kept */
export function applyWorkout(g: GymBoosts | undefined, w: Workout, time: number, until: number, /** the bag: the share of beats landed */ score = 1): GymBoosts {
  const out: GymBoosts = {};
  for (const k of Object.keys(BOOST) as BoostKey[]) { const b = g?.[k]; if (b && time < b.until) out[k] = { ...b }; }
  if (w === 'sauna' || (w === 'boxing' && score <= 0)) return out; // (the sauna heals now; it leaves no boost)
  const keys: BoostKey[] = w === 'full' ? ['upper', 'lower', 'cardio'] : w === 'boxing' ? ['melee'] : [w], amt = w === 'full' ? 0.5 : w === 'boxing' ? Math.round(Math.min(1, score) * 100) / 100 : 1;
  for (const k of keys) { const b = out[k]; out[k] = !b ? { until, amt } : b.amt > amt ? b : { until: Math.max(until, b.until), amt }; }
  return out;
}
/** the boosts still running (for a save or the wire): undefined when none */
export function liveBoosts(g: GymBoosts | undefined, time: number): GymBoosts | undefined { if (!g) return undefined; const out: GymBoosts = {}; for (const k of Object.keys(BOOST) as BoostKey[]) if (boostOf(g, k, time) > 0) out[k] = g[k]; return Object.keys(out).length ? out : undefined; }

/** the strength in play: the build, times the upper-body boost while it lasts */
export function strengthOf(look: Look | undefined, gym: GymBoosts | undefined, time: number): number { return bodyStrength(look) * (1 + BOOST.upper * boostOf(gym, 'upper', time)); }
/** the legs: a multiplier on foot speed from the lower-body boost */
export function legsOf(gym: GymBoosts | undefined, time: number): number { return 1 + BOOST.lower * boostOf(gym, 'lower', time); }
/** endurance: how much longer stamina lasts and how much sooner it comes back */
export function enduranceOf(gym: GymBoosts | undefined, time: number): number { return 1 + BOOST.cardio * boostOf(gym, 'cardio', time); }
/** (Sep 25) the swing: a multiplier from the punching bag's boost */
export function meleeOf(gym: GymBoosts | undefined, time: number): number { return 1 + BOOST.melee * boostOf(gym, 'melee', time); }

// ---- the punching bag (Sep 25): a beat every BAG_BEAT seconds; a punch near one lands it ----
export const BAG_BEAT = 1.2, BAG_LEAD = 1.5;
/** a punch counts from this far before its beat to this far after (the after is wider: online the press arrives a round trip late) */
export const BAG_WINDOW = { early: 0.2, late: 0.45 };
/** the beats of a round `need` seconds long */
export function bagBeats(need: number): number[] { const out: number[] = []; for (let t = BAG_LEAD; t <= need - 0.6; t += BAG_BEAT) out.push(Math.round(t * 100) / 100); return out; }
/** the beat a punch at `t` lands on (not one already landed), or -1: a miss */
export function judgePunch(t: number, beats: number[], landed: number[]): number {
  let best = -1, bd = Infinity;
  beats.forEach((b, i) => { const d = t - b; if (d < -BAG_WINDOW.early || d > BAG_WINDOW.late || landed.includes(i)) return; if (Math.abs(d) < bd) { bd = Math.abs(d); best = i; } });
  return best;
}

// ---- the sauna (Sep 25): a second of löyly ----
export const SAUNA = { hp: 4, stamina: 0.25, water: 0.012 };

// ---- the week's hardest hitters, per world (Sep 25) ----
export interface BoardEntry { name: string; hits: number; of: number; week: number }
export const weekOf = (day: number): number => Math.floor(day / 7);
/** a round into the board: one line a name (their best), this week's only, the best ten */
export function boardAdd(board: BoardEntry[], e: BoardEntry): BoardEntry[] {
  const out = board.filter((x) => x.week === e.week);
  const i = out.findIndex((x) => x.name === e.name);
  if (i >= 0) { if (out[i].hits >= e.hits) return out; out.splice(i, 1); }
  out.push(e); out.sort((a, b) => b.hits - a.hits || a.of - b.of || (a.name < b.name ? -1 : 1));
  return out.slice(0, 10);
}
/** where a name stands (1 = the best), or 0 */
export const boardRank = (board: BoardEntry[], name: string): number => board.findIndex((x) => x.name === name) + 1;

/** kg you carry before it starts to cost (food, water, then the run) — 20 for an average build */
export function carryLimit(str: number): number { return 20 * str; }
/** kg past which you cannot run — 35 for an average build */
export function sprintLimit(str: number): number { return 35 * str; }

/** stamina: a full meter is 14 s of sprinting; a swing takes 7 %; walking gives back 4 % a second, standing still 12 %; winded until back at a quarter */
export const STAMINA = { sprint: 1 / 14, swing: 0.07, walk: 0.04, rest: 0.12, recover: 0.25, weakSwing: 0.6 };
/** a second of stamina: spent by a sprint, given back by walking or (faster) standing still; `endurance` stretches both */
export function stepStamina(s: number, winded: boolean, act: { sprinting: boolean; moving: boolean }, endurance: number, dt: number): { s: number; winded: boolean } {
  let n = act.sprinting ? s - (STAMINA.sprint / endurance) * dt : s + (act.moving ? STAMINA.walk : STAMINA.rest) * endurance * dt;
  n = Math.max(0, Math.min(1, n));
  return { s: n, winded: n <= 0 ? true : winded && n < STAMINA.recover };
}
